/**
 * @file auction-dates.ts
 * @description 매각일정 계산 유틸리티
 *
 * 주요 기능:
 * 1. Property의 auctionStep을 기반으로 매각일정 계산
 * 2. 각 회차별 매각일 계산 (14일 간격)
 * 3. 날짜 포맷팅 (YYYY.MM.DD)
 *
 * 계산 로직:
 * - 현재 회차(auctionStep)부터 5회차까지 계산
 * - 각 회차는 14일 간격 (2주)
 * - 기준일은 현재 날짜 또는 지정된 날짜
 *
 * 사용 예시:
 * ```ts
 * import { calculateAuctionDates } from "@/lib/domain/auction/utils/auction-dates";
 * const dates = calculateAuctionDates(2); // 2회차부터 5회차까지
 * // [{ step: 2, date: "2024.11.26" }, { step: 3, date: "2024.12.10" }, ...]
 * ```
 *
 * @dependencies
 * - 없음 (순수 함수)
 *
 * @see {@link /docs/domain/glossary.md} - 경매 용어 정의
 */

/**
 * 매각일정 정보
 */
export interface AuctionDate {
  /** 경매 회차 (1~5) */
  step: number;
  /** 매각일 (YYYY.MM.DD 형식) */
  date: string;
}

/**
 * 매각일정 계산
 * Property의 auctionStep을 기반으로 현재 회차부터 5회차까지의 매각일정을 계산합니다.
 *
 * @param auctionStep - 현재 경매 회차 (1~5)
 * @param baseDate - 기준일 (선택, 없으면 오늘 날짜)
 * @returns 매각일정 배열 (현재 회차부터 5회차까지)
 *
 * @example
 * ```ts
 * // 1회차인 경우
 * calculateAuctionDates(1)
 * // [{ step: 1, date: "2024.11.26" }, { step: 2, date: "2024.12.10" }, ...]
 *
 * // 2회차인 경우
 * calculateAuctionDates(2)
 * // [{ step: 2, date: "2024.11.26" }, { step: 3, date: "2024.12.10" }, ...]
 * ```
 */
export function calculateAuctionDates(
  auctionStep: number,
  baseDate?: Date,
): AuctionDate[] {
  // auctionStep 유효성 검증
  const step = Math.max(1, Math.min(5, auctionStep));
  
  // 기준일 설정 (없으면 오늘)
  const startDate = baseDate ? new Date(baseDate) : new Date();
  
  // 결과 배열
  const dates: AuctionDate[] = [];
  
  // 현재 회차부터 5회차까지 계산
  for (let i = 0; i <= 5 - step; i++) {
    const currentStep = step + i;
    const auctionDate = new Date(startDate);
    
    // 각 회차는 14일 간격 (2주)
    // 현재 회차는 기준일, 다음 회차는 +14일, 그 다음은 +28일...
    auctionDate.setDate(auctionDate.getDate() + (i * 14));
    
    dates.push({
      step: currentStep,
      date: formatDate(auctionDate),
    });
  }
  
  return dates;
}

/**
 * 날짜 포맷팅 (YYYY.MM.DD)
 *
 * @param date - 포맷팅할 날짜
 * @returns YYYY.MM.DD 형식의 문자열
 *
 * @example
 * ```ts
 * formatDate(new Date(2024, 10, 26)) // "2024.11.26"
 * ```
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
}

/**
 * 날짜 문자열을 YYYY.MM.DD 형식으로 포맷팅
 * ISO 형식(YYYY-MM-DD) 또는 Date 객체를 받아서 YYYY.MM.DD 형식으로 변환
 *
 * @param dateString - 날짜 문자열 (YYYY-MM-DD 형식) 또는 Date 객체
 * @returns YYYY.MM.DD 형식의 문자열
 *
 * @example
 * ```ts
 * formatDateString("2024-11-26") // "2024.11.26"
 * formatDateString(new Date()) // "2024.11.26"
 * ```
 */
export function formatDateString(dateString: string | Date): string {
  try {
    const date = typeof dateString === "string" ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) {
      return dateString.toString(); // 유효하지 않은 날짜면 원본 반환
    }
    return formatDate(date);
  } catch {
    return dateString.toString(); // 에러 발생 시 원본 반환
  }
}

